/**
 * «Сохранить изменения?» (handoff §15.5): one line per change, then «Сохранить» and
 * «Вернуться к правке». A bottom sheet on a phone, a centred dialog on desktop (the
 * same split as the delete confirmation).
 *
 * The note about an unfinished practice run is not here: the server exposes no run
 * to tell this sheet about until practice exists (the player task adds it).
 *
 * `cards` is the edit's new cards and what the budget allows (ADR-115 Amendment 3),
 * under the lines. `blocked` holds «Сохранить» back only while the cards have no deck.
 */
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Pencil } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import { useIsDesktop } from '../../../hooks/useIsDesktop'
import { useSwipeNavBlock } from '../../../hooks/useSwipeNavBlock'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../../utils/controlSurface'
import type { Change } from '../../../utils/textEdit'
import { PrimaryButton, SecondaryPill } from '../ui'

type Glyph = 'minus' | 'plus' | 'pencil'

function describeChange(c: Change, tr: ReturnType<typeof useLocale>['tr']): { text: string; glyph: Glyph } {
  const te = tr.textEdit
  switch (c.kind) {
    case 'title': return { text: te.lineTitle(c.title), glyph: 'pencil' }
    case 'body': return { text: te.lineBody, glyph: 'pencil' }
    case 'gapRemoved': return { text: te.lineGapRemoved(c.form), glyph: 'minus' }
    case 'gapDropped': return { text: te.lineGapDropped(c.form), glyph: 'minus' }
    case 'gapNew': return { text: te.lineGapNew(c.form), glyph: 'plus' }
    case 'places': return { text: te.linePlaces(c.form, c.count), glyph: 'plus' }
    case 'deckLinked': return { text: te.lineDeckLinked(c.title), glyph: 'plus' }
    case 'deckUnlinked': return { text: te.lineDeckUnlinked(c.title), glyph: 'minus' }
    case 'deckNew': return { text: te.lineDeckNew(c.title), glyph: 'plus' }
    case 'answerAdded': return { text: te.lineAnswerAdded(c.answer, c.form), glyph: 'plus' }
    case 'answerRemoved': return { text: te.lineAnswerRemoved(c.answer, c.form), glyph: 'minus' }
    case 'label': return { text: te.lineLabel(c.form, tr.learn.formLabel(c.label)), glyph: 'pencil' }
    case 'labelHidden': return { text: te.lineLabelHidden(c.form), glyph: 'pencil' }
    case 'labelReset': return { text: te.lineLabelReset(c.form), glyph: 'pencil' }
    case 'waiting': return { text: te.lineWaiting(c.word), glyph: 'minus' }
  }
}

export default function SaveSheet({ changes, pending, onSave, onBack, cards = null, blocked = false }: {
  changes: Change[]
  pending: boolean
  onSave: () => void
  onBack: () => void
  cards?: ReactNode
  blocked?: boolean
}) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const desktop = useIsDesktop()
  // Mounted only while open (ADR-106 P3).
  useSwipeNavBlock(true, 'text-edit-save')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !pending) onBack() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBack, pending])

  const lines = changes.length > 0 && (
    <ul data-testid="edit-save-lines" style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {changes.map((c, i) => {
        const { text, glyph } = describeChange(c, tr)
        return (
          <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14, lineHeight: 1.45, color: 'var(--text-primary)' }}>
            <span
              aria-hidden
              style={{
                width: 16, flexShrink: 0, textAlign: 'center', fontWeight: 700,
                color: glyph === 'plus' ? 'var(--accent)' : 'var(--text-tertiary)',
              }}
            >
              {glyph === 'plus' ? '+' : glyph === 'minus' ? '−' : <Pencil size={13} style={{ marginTop: 4 }} />}
            </span>
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{text}</span>
          </li>
        )
      })}
    </ul>
  )
  const title = <h2 id="edit-save-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{te.saveTitle}</h2>
  const dialog = { role: 'dialog' as const, 'aria-modal': true as const, 'aria-labelledby': 'edit-save-title' }

  const content = desktop ? (
    <div
      className={`add-sheet-scrim ${CONTROL_SURFACE}`}
      onContextMenu={suppressTouchContextMenu}
      onClick={() => { if (!pending) onBack() }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        {...dialog}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-float)',
          padding: 24, maxWidth: 460, width: '100%', maxHeight: '80dvh', overflowY: 'auto', fontFamily: 'var(--font-ui)',
        }}
      >
        {title}
        {lines}
        {cards}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <SecondaryPill height={40} onClick={onBack} disabled={pending} data-testid="edit-save-back">{te.saveBack}</SecondaryPill>
          <PrimaryButton height={40} onClick={onSave} disabled={pending || blocked} data-testid="edit-save-confirm" style={{ padding: '0 22px', fontSize: 14 }}>
            {pending ? te.saving : te.saveConfirm}
          </PrimaryButton>
        </div>
      </div>
    </div>
  ) : (
    <div className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={() => { if (!pending) onBack() }} className="add-sheet-scrim" style={{ position: 'absolute', inset: 0 }} />
      <div
        {...dialog}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '85dvh', overflowY: 'auto',
          background: 'var(--bg-surface)', borderRadius: '16px 16px 0 0', boxShadow: 'var(--shadow-float)',
          fontFamily: 'var(--font-ui)', padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
        }}
      >
        <div aria-hidden style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />
        {title}
        {lines}
        {cards}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
          <PrimaryButton full height={52} onClick={onSave} disabled={pending || blocked} data-testid="edit-save-confirm">
            {pending ? te.saving : te.saveConfirm}
          </PrimaryButton>
          <SecondaryPill full height={48} onClick={onBack} disabled={pending} data-testid="edit-save-back">{te.saveBack}</SecondaryPill>
        </div>
      </div>
    </div>
  )
  return createPortal(content, document.body)
}
